import React from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Avatar,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import EmptyState from '@/components/shared/EmptyState'
import type { AccountUser, Account } from '@/types'

interface Props {
  members: AccountUser[]
}

export default function AccountUsersIndex({ members }: Props) {
  const { currentAccount, isAccountAdmin, auth } = usePage<{
    currentAccount: Account
    isAccountAdmin: boolean
    auth: { user: { id: number; email: string } }
  }>().props

  return (
    <ManagementLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Team Members
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage who has access to this account.
          </Typography>
        </Box>
        {isAccountAdmin && (
          <Button
            component={Link}
            href={`/accounts/${currentAccount.slug}/members/new`}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Add Member
          </Button>
        )}
      </Box>

      {members.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Joined</TableCell>
                {isAccountAdmin && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                        {member.user.email[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{member.user.email}</Typography>
                        {member.user.id === auth.user.id && (
                          <Typography variant="caption" color="text.secondary">You</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={member.role === 'admin' ? 'Admin' : 'Member'}
                      size="small"
                      color={member.role === 'admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : '-'}
                  </TableCell>
                  {isAccountAdmin && (
                    <TableCell align="right">
                      {member.user.id !== auth.user.id && (
                        <IconButton
                          component={Link}
                          href={`/accounts/${currentAccount.slug}/members/${member.id}/edit`}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon="users"
          title="No team members"
          description="You're the only member of this account."
          actionPath={isAccountAdmin ? `/accounts/${currentAccount.slug}/members/new` : undefined}
          actionText={isAccountAdmin ? 'Add Member' : undefined}
        />
      )}
    </ManagementLayout>
  )
}
